import { useMemo, useState } from "react";
import Button from "../../common/Button";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/manipulateText";
import { faClose, faEdit } from "@fortawesome/free-solid-svg-icons";
import NewsItemModal from "./NewsItemModal";
import { calculateSingleNewsEstimatedTime } from "../helpers";

const EditableNewsItem = ({
  news,
  isSelected,
  onToggleSelect,
  onEdit,
  onRemove,
  videoRestrictions,
  isReadonly,
  preview,
  selectedVideoTemplate,
  styleSettings,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isTitleAvailable = !!videoRestrictions["items[].title"];
  const isTitleValid = isTitleAvailable
    ? news.title?.length <= videoRestrictions["items[].title"]?.maxLength
    : true;

  const isSubTitleAvailable = !!videoRestrictions["items[].subTitle"];
  const isSubTitleValid = isSubTitleAvailable
    ? news.subTitle?.length <= videoRestrictions["items[].subTitle"]?.maxLength
    : true;

  const isVoiceTextAvailable = !!videoRestrictions["items[].voiceText"];

  const isVoiceTextValid = isVoiceTextAvailable
    ? news.voiceText?.length <=
      videoRestrictions["items[].voiceText"]?.maxLength
    : true;

  const isCategoryAvailable = !!videoRestrictions["items[].category"];

  const isCategoryValid = isCategoryAvailable
    ? news.category?.length <= videoRestrictions["items[].category"]?.maxLength
    : true;

  const isImageAvailable = !!videoRestrictions["items[].image"];
  const isImageValid = isImageAvailable;

  const isValid =
    isTitleValid &&
    isSubTitleValid &&
    isVoiceTextValid &&
    isImageValid &&
    isCategoryValid;

  const isRemovable = useMemo(
    () => typeof news?.id === "string" && news?.id?.startsWith("new-card-"),
    [news?.id]
  );

  const estimatedTime = useMemo(() => {
    return calculateSingleNewsEstimatedTime(news);
  }, [news]);

  const handleSave = (updatedNews) => {
    if (updatedNews) {
      Object.entries(updatedNews).forEach(([key, value]) => {
        if (key === "id") {
          return;
        }
        if (news[key] !== value) {
          onEdit?.(news.id, key, value);
        }
      });
    }
    setIsModalOpen(false);
  };

  return (
    <div
      className={`bg-white rounded-lg p-4 mb-3 hover:shadow-md transition-shadow relative`}
      onClick={() => {
        if (!isValid) {
          return;
        }
        onToggleSelect(news.id);
      }}
    >
      <div
        className={`
          absolute inset-0 pointer-events-none rounded-lg 
          border-4 transition-all
          ${
            isSelected
              ? "border-empower-500"
              : !isValid
              ? "border-red-500"
              : "border-transparent"
          }
        `}
        style={{ zIndex: 2 }}
      ></div>
      <div className="flex flex-col items-stretch space-x-3">
        <div className="flex flex-row items-center justify-between mb-2">
          <div className="text-xs text-gray-500 mt-2">
            {isValid
              ? t(
                  "this news item is valid. you can add it to your video by clicking on it."
                )
              : t(
                  "this news item is invalid. please check the news item and make it valid by pressing the edit button."
                )}
          </div>
          <div className="flex-shrink-0 flex flex-row gap-2">
            <Button
              color="inverse_primary"
              disabled={isReadonly}
              onClick={() => setIsModalOpen(true)}
              icon={faEdit}
              type="button"
            />
            <Button
              color="danger"
              disabled={!isRemovable || isReadonly}
              onClick={onRemove}
              icon={faClose}
              type="button"
            />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {preview && preview(news)}
          <div className="text-xs text-gray-600 italic border-l-4 pl-2 bg-gray-100 rounded-r-md">
            {news.voiceText}
          </div>
          {news.voiceText && (
            <div className="text-xs text-gray-500 mt-2">
              {t("estimated video duration")}:{" "}
              <span className="font-semibold">{estimatedTime}</span>
            </div>
          )}
          {news?.newsDate && (
            <div className="text-xs text-gray-500 mt-2">
              <b>{t("news date")}:</b> {formatDate(news.newsDate)}
            </div>
          )}
        </div>
      </div>
      <NewsItemModal
        isVisible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        news={news}
        selectedVideoTemplate={selectedVideoTemplate}
        styleSettings={styleSettings}
        videoRestrictions={videoRestrictions}
        isReadonly={isReadonly}
        onSave={handleSave}
      />
    </div>
  );
};

export default EditableNewsItem;
